import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  LONG_WAIT_COMMAND_TYPE,
  LONG_WAIT_THRESHOLD_SECONDS,
  type DeviceCommandStatus as DeviceCommandStatusModel,
  type OperatorDeviceCommandType,
} from '@ghostscreen/contracts';
import { api } from '../../lib/api';

const POLL_INTERVAL_MS = 3000;
// Canonical rule lives in @ghostscreen/contracts (shared with the
// command-operations aggregation query) -- do not hardcode 90 or
// 'RESTART_PLAYER' here.
const LONG_WAIT_MS = LONG_WAIT_THRESHOLD_SECONDS * 1000;
const TERMINAL_STATUSES = new Set(['executed', 'failed', 'expired']);

const STATUS_LABEL_KEY: Record<
  string,
  | 'fleet.commandStatus.pending'
  | 'fleet.commandStatus.sent'
  | 'fleet.commandStatus.acknowledged'
  | 'fleet.commandStatus.executed'
> = {
  pending: 'fleet.commandStatus.pending',
  sent: 'fleet.commandStatus.sent',
  acknowledged: 'fleet.commandStatus.acknowledged',
  executed: 'fleet.commandStatus.executed',
};

export type DeviceCommandWireStatus = DeviceCommandStatusModel['status'];

type DeviceCommandStatusProps = {
  organisationId: string;
  deviceId: string;
  commandId: string;
  commandType: OperatorDeviceCommandType;
  /** Fires whenever the polled command status changes (including first load). */
  onStatusChange?: (status: DeviceCommandWireStatus) => void;
};

/**
 * Polls GET /commands/:commandId every 3s while the command is non-terminal.
 * A polling/network error is shown distinctly from a device-reported
 * command failure and never stops or resets the poll.
 */
export function DeviceCommandStatus({
  organisationId,
  deviceId,
  commandId,
  commandType,
  onStatusChange,
}: DeviceCommandStatusProps) {
  const { t } = useTranslation('displays');
  const [now, setNow] = useState(() => Date.now());

  const query = useQuery({
    queryKey: ['device-command-status', organisationId, deviceId, commandId],
    queryFn: () => api.getCommandStatus(organisationId, deviceId, commandId),
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      if (status && TERMINAL_STATUSES.has(status)) return false;
      return POLL_INTERVAL_MS;
    },
  });

  const data = query.data;
  const status = data?.status;

  useEffect(() => {
    if (!status || !onStatusChange) return;
    onStatusChange(status);
  }, [status, onStatusChange]);

  // Ticks locally so the long-wait caveat can appear between polls, not just on refetch.
  // Stops once the command reaches a terminal status, to avoid unnecessary rerenders
  // after the command has finished.
  useEffect(() => {
    if (status && TERMINAL_STATUSES.has(status)) return;
    const interval = setInterval(() => setNow(Date.now()), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [status]);

  if (query.isPending) {
    return <p className="mt-1 text-xs text-muted">{t('fleet.commandStatus.checking')}</p>;
  }

  // First load failed outright — nothing to show yet, but keep trying.
  if (query.isError && !data) {
    return <p className="mt-1 text-xs text-amber-600">{t('fleet.commandStatus.checkFailed')}</p>;
  }

  if (!status) return null;

  const referenceTime = data?.sentAt ?? data?.createdAt;
  const elapsedMs = referenceTime ? now - new Date(referenceTime).getTime() : 0;
  const showLongWaitCaveat =
    commandType === LONG_WAIT_COMMAND_TYPE &&
    (status === 'pending' || status === 'sent') &&
    elapsedMs >= LONG_WAIT_MS;

  if (status === 'failed') {
    return (
      <p className="mt-1 text-xs text-red-600">
        {t('fleet.commandStatus.failed', {
          reason: data?.resultMessage ?? t('fleet.commandStatus.noReason'),
        })}
      </p>
    );
  }

  if (status === 'expired') {
    return <p className="mt-1 text-xs text-amber-700">{t('fleet.commandStatus.expired')}</p>;
  }

  const statusLabelKey = STATUS_LABEL_KEY[status];

  return (
    <div className="mt-1 space-y-0.5">
      <p
        className={
          status === 'executed' ? 'text-xs font-semibold text-emerald-700' : 'text-xs text-muted'
        }
      >
        {statusLabelKey ? t(statusLabelKey) : status}
      </p>
      {showLongWaitCaveat && (
        <p className="text-xs text-amber-600">{t('fleet.commandStatus.longWait')}</p>
      )}
      {query.isError && (
        <p className="text-xs text-amber-600">{t('fleet.commandStatus.refreshTrouble')}</p>
      )}
    </div>
  );
}
